import { create } from 'zustand';
import type { CalibrationConfig } from '../utils/calibrationUtils';
import { DEFAULT_CALIBRATION } from '../utils/calibrationUtils';
import api from '../services/api';

const STORAGE_KEY = 'calibration_config';

/** 從 localStorage 載入快取配置（同步、零延遲） */
function getInitialConfig(): CalibrationConfig {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) return JSON.parse(cached);
  } catch (e) { console.warn('[Calibration] Failed to load config:', e); }
  return DEFAULT_CALIBRATION;
}

interface CalibrationState {
  config: CalibrationConfig;
  showRaw: boolean;
  loading: boolean;
  ready: boolean;
  fetchConfig: () => Promise<void>;
  updateConfig: (config: CalibrationConfig) => Promise<void>;
  disableCalibration: () => Promise<void>;
  toggleShowRaw: () => void;
}

const useCalibrationStore = create<CalibrationState>((set) => ({
  config: getInitialConfig(),
  showRaw: false,
  loading: false,
  ready: true,

  fetchConfig: async () => {
    try {
      const { data } = await api.get('/calibration');
      if (data.success && data.data) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data.data));
        set({ config: data.data, ready: true });
      }
    } catch {
      // API 失敗（未登入 401 等）保持現有配置，不回退
    }
  },

  updateConfig: async (config) => {
    set({ loading: true });
    try {
      await api.put('/calibration', {
        enabled: config.enabled,
        ehi_target_min: config.ehi?.target_min ?? 65,
        ehi_target_max: config.ehi?.target_max ?? 95,
        vibration_scales: config.vibration ?? {},
        suppress_alarms: config.suppress_alarms ?? true,
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
      set({ config, loading: false });
    } catch {
      set({ loading: false });
      throw new Error('校準配置更新失敗');
    }
  },

  disableCalibration: async () => {
    try {
      await api.delete('/calibration');
      localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_CALIBRATION));
      set({ config: DEFAULT_CALIBRATION });
    } catch {
      /* ignore */
    }
  },

  toggleShowRaw: () => set((s) => ({ showRaw: !s.showRaw })),
}));

export default useCalibrationStore;
